import { useState, useEffect } from 'react'
import { Truck, MapPin, Phone, Mail, ExternalLink, Apple, Coffee, Cake, Gift } from 'lucide-react'
import { getSuppliers, MapLocation } from '../api/map'
import { resolveImageUrl } from '../utils/imageUrl'

const CHAIN_NAMES = [
  'aldi',
  'amazon fresh',
  "bj's",
  'costco',
  'food lion',
  'fresh market',
  'harris teeter',
  'kroger',
  'lidl',
  'lowes foods',
  "sam's club",
  'sprouts',
  'target',
  "trader joe's",
  'trader joes',
  'walmart',
  'wegmans',
  'whole foods',
  'publix'
]

const supplierLabel = (supplier: MapLocation) => {
  if (supplier.businessType && supplier.businessType.toLowerCase() !== 'other') {
    return supplier.businessType
  }
  const name = supplier.name.toLowerCase()
  return CHAIN_NAMES.some(chain => name.includes(chain)) ? 'Chain supplier' : 'Local supplier'
}

const hasPublicEmail = (email?: string) => {
  if (!email) return false
  const normalized = email.toLowerCase()
  return (
    !normalized.endsWith('@import.harvesthelp.local') &&
    !normalized.endsWith('@harvesthelp.app')
  )
}

const SuppliersPage = () => {
  const [suppliers, setSuppliers] = useState<MapLocation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        setLoading(true)
        const data = await getSuppliers()
        setSuppliers(data)
      } catch (err) {
        setError('Failed to load suppliers')
        console.error('Error fetching suppliers:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchSuppliers()
  }, [])

  const formatAddress = (supplier: MapLocation) => {
    return `${supplier.street}, ${supplier.city}, ${supplier.state} ${supplier.zipCode}`
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading suppliers...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Truck className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading suppliers</h3>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Food Decorations */}
      <div className="fixed inset-0 pointer-events-none opacity-5 z-0">
        <Apple className="absolute top-16 left-10 h-6 w-6 text-red-500 animate-pulse" />
        <Coffee className="absolute top-36 right-14 h-8 w-8 text-amber-600 animate-bounce" />
        <Cake className="absolute bottom-36 left-20 h-8 w-8 text-orange-400 animate-pulse" />
        <Gift className="absolute bottom-16 right-28 h-6 w-6 text-green-500 animate-bounce" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        {/* Header */}
        <div className="mb-10 rounded-2xl border border-secondary-100 bg-gradient-to-r from-secondary-50 via-white to-primary-50 px-6 py-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Truck className="h-8 w-8 text-secondary-600 mr-3" />
            Suppliers
            <Coffee className="ml-3 h-6 w-6 text-amber-600" />
          </h1>
          <p className="text-gray-600 mt-2 max-w-2xl">
            Discover independent businesses and regional chains helping move surplus food to the
            community.
          </p>
        </div>

        {/* Suppliers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {suppliers.map((supplier) => (
            <article
              key={supplier.id}
              className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all relative overflow-hidden"
            >
              {supplier.profileImage ? (
                <div className="h-48 w-full overflow-hidden bg-neutral-100">
                  <img
                    src={resolveImageUrl(supplier.profileImage)}
                    alt={supplier.name}
                    className="h-full w-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none'
                    }}
                  />
                </div>
              ) : (
                <div className="h-32 w-full bg-gradient-to-br from-secondary-50 to-primary-100 flex items-center justify-center">
                  <Truck className="h-12 w-12 text-secondary-500" />
                </div>
              )}
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="h-11 w-11 rounded-xl bg-secondary-50 flex items-center justify-center mr-3 shrink-0">
                    <Truck className="h-6 w-6 text-secondary-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900">{supplier.name}</h3>
                    <span className="mt-1 inline-flex rounded-full bg-primary-50 px-2.5 py-1 text-xs font-semibold text-primary-700">
                      {supplierLabel(supplier)}
                    </span>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                    {formatAddress(supplier)}
                  </div>
                  {supplier.phone && (
                    <a
                      href={`tel:${supplier.phone}`}
                      className="flex items-center text-sm text-gray-600 hover:text-secondary-700"
                    >
                      <Phone className="h-4 w-4 mr-2 text-gray-400" />
                      {supplier.phone}
                    </a>
                  )}
                  {hasPublicEmail(supplier.email) && (
                    <a
                      href={`mailto:${supplier.email}`}
                      className="flex items-center text-sm text-gray-600 hover:text-secondary-700 break-all"
                    >
                      <Mail className="h-4 w-4 mr-2 text-gray-400" />
                      {supplier.email}
                    </a>
                  )}
                  {supplier.contactUrl && (
                    <a
                      href={supplier.contactUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center rounded-lg bg-secondary-50 px-3 py-2 text-sm font-semibold text-secondary-700 hover:bg-secondary-100"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Contact Website
                    </a>
                  )}
                  {supplier.description && (
                    <p className="text-sm text-gray-600 mt-2">{supplier.description}</p>
                  )}
                </div>

              </div>
            </article>
          ))}
        </div>

        {/* Empty State */}
        {suppliers.length === 0 && (
          <div className="text-center py-12">
            <Truck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No suppliers found</h3>
            <p className="text-gray-600">Check back later or contact us to add your business as a supplier.</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default SuppliersPage 